import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, X as XIcon, AlertCircle } from 'lucide-react';
import { uploadAPI } from '../../utils/api';
import { usePreventScroll } from '../../utils/hooks';
import { useVat } from '../../contexts/VatContext';
import { useTheme } from '../../contexts/ThemeContext';
import AllergenMultiSelect from '../Allergens/AllergenMultiSelect.jsx';

const MaterialForm = ({ material, categories, currentCategoryId, onSave, onCancel }) => {
  const { t, i18n } = useTranslation();
  const { calculatePriceWithVat, calculatePriceWithoutVat } = useVat();
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    brand_name: '',
    main_unit: '',
    quantity_per_main_unit: '',
    quantity_per_piece: '',
    sub_unit: '',
            // main_price field removed
    price_with_vat: '',
    price_without_vat: '',
    supplier: '',
    category_id: '',
    image_url: '',
    calories: '',
    serving_size: '',
    allergens: []
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null); // Store the selected file for later upload
  const [uploading, setUploading] = useState(false);
  const [uploadController, setUploadController] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadedImagePublicId, setUploadedImagePublicId] = useState(null);
  const [originalImagePublicId, setOriginalImagePublicId] = useState(null); // Track original image when editing
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSubUnitOpen, setIsSubUnitOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const modalContentRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const subUnitDropdownRef = useRef(null);
  const scrollableAreaRef = useRef(null);
  const imageDeletedRef = useRef(false); // Track if image was already deleted
  const fileInputRef = useRef(null); // Ref for file input to reset it after error

  // Prevent background scrolling when modal is open
  usePreventScroll(isModalOpen);

  // Function to extract public ID from Cloudinary URL
  const extractPublicIdFromUrl = (url) => {
    if (!url) return null;
    try {
      // Extract public ID from Cloudinary URL
      // Format: https://res.cloudinary.com/cloud_name/image/upload/v123456789/folder/image.jpg
      const match = url.match(/\/upload\/[^\/]+\/(.+)$/);
      if (match) {
        let publicId = match[1];
        // Remove file extension from public ID (Cloudinary public IDs don't include extensions)
        publicId = publicId.replace(/\.[^/.]+$/, '');
        return publicId;
      }
      return null;
    } catch (error) {
      console.error('Error extracting public ID from URL:', error);
      return null;
    }
  };

  // Function to clean up uploaded image from Cloudinary
  const cleanupUploadedImage = async () => {
    if (uploadedImagePublicId && !material?.image_url && !imageDeletedRef.current) {
      // Only delete if this is a new upload (not editing existing material) and not already deleted
      imageDeletedRef.current = true; // Mark as deleted immediately to prevent duplicate calls
      try {
        console.log('🧹 Cleanup: Deleting uploaded image:', uploadedImagePublicId);
        await uploadAPI.deleteImage(uploadedImagePublicId);
        console.log('✅ Cleanup: Image cleaned up successfully');
      } catch (error) {
        console.error('❌ Cleanup: Failed to cleanup image:', error);
        // Don't show error to user as this is cleanup
      }
    } else {
      console.log('ℹ️ Cleanup: Skipping image deletion (no uploaded image, editing existing, or already deleted)');
    }
    
    // Clear selectedFile if user cancels without saving
    // No cleanup needed for selectedFile since it's just a local file reference
    if (selectedFile) {
      setSelectedFile(null);
      setImagePreview(null);
    }
  };

  // Cleanup uploaded image when component unmounts (only for uploaded images, not selected files)
  useEffect(() => {
    return () => {
      if (uploadedImagePublicId && !material?.image_url && !imageDeletedRef.current) {
        // Cleanup on unmount if image was uploaded but not saved and not already cleaned up
        cleanupUploadedImage();
      }
    };
  }, []); // Only run on mount/unmount, not when uploadedImagePublicId changes



  // Handle clicks outside category dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
    };

    if (isCategoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoryOpen]);

  // Handle clicks outside sub unit dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subUnitDropdownRef.current && !subUnitDropdownRef.current.contains(event.target)) {
        setIsSubUnitOpen(false);
      }
    };

    if (isSubUnitOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSubUnitOpen]);

  // Handle clicks outside modal content and prevent scrolling
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Allow file input clicks to work properly
      if (event.target.type === 'file' || event.target.closest('input[type="file"]') || event.target.closest('label')) {
        return;
      }
      
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        // Clicked outside modal content - prevent default behavior
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleScroll = (event) => {
      // Only allow scrolling within the modal content
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };



    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, { passive: false });

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);





  const handleOverlayClick = (event) => {
    // Allow file input clicks to work properly
    if (event.target.type === 'file' || event.target.closest('input[type="file"]')) {
      return;
    }
    
    // Prevent closing when clicking outside - do nothing
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    if (material) {
      setFormData({
        name_en: material.name_en || '',
        name_ar: material.name_ar || '',
        brand_name: material.brand_name || '',
        main_unit: material.main_unit || '',
        quantity_per_main_unit: material.quantity_per_main_unit || '',
        quantity_per_piece: material.quantity_per_piece || '',
        sub_unit: material.sub_unit || '',
        // main_price field removed
        price_with_vat: material.price_with_vat || '',
        price_without_vat: material.price_without_vat || '',
        supplier: material.supplier || '',
        category_id: material.category_id !== undefined ? material.category_id : '',
        image_url: material.image_url || '',
        calories: material.calories || '',
        serving_size: material.serving_size || '',
        allergens: material.allergens || []
      });
      setImagePreview(material.image_url || null);
      
      // Extract and store the original image's public ID for potential deletion
      const originalPublicId = extractPublicIdFromUrl(material.image_url);
      setOriginalImagePublicId(originalPublicId);
      console.log('📸 Original image public ID extracted:', originalPublicId);
    } else {
      // Set default category for new materials
      setFormData({
        name_en: '',
        name_ar: '',
        brand_name: '',
        main_unit: '',
        quantity_per_main_unit: '',
        quantity_per_piece: '',
        sub_unit: '',
        // main_price field removed
        price_with_vat: '',
        price_without_vat: '',
        supplier: '',
        category_id: currentCategoryId !== undefined ? currentCategoryId : '',
        image_url: '',
        calories: '',
        serving_size: '',
        allergens: []
      });
      setImagePreview(null);
    }
  }, [material, currentCategoryId]);

  // Auto-calculate price with VAT when price without VAT is set initially
  useEffect(() => {
    if (material && material.price_without_vat && !material.price_with_vat) {
      const priceWithVat = calculatePriceWithVat(material.price_without_vat);
      setFormData(prev => ({
        ...prev,
        price_with_vat: priceWithVat
      }));
    }
  }, [material, calculatePriceWithVat]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('Form field changed:', { name, value });
    
    // Convert category_id to number if it's not empty
    const processedValue = name === 'category_id' && value !== '' ? parseInt(value, 10) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // VAT calculation rules
    if (name === 'price_with_vat') {
      if (value && !isNaN(parseFloat(value))) {
        // When price with VAT is entered, calculate price without VAT
        const priceWithoutVat = calculatePriceWithoutVat(value);
        setFormData(prev => ({
          ...prev,
          price_without_vat: priceWithoutVat
        }));
      } else {
        // When price with VAT is cleared, clear price without VAT too
        setFormData(prev => ({
          ...prev,
          price_without_vat: ''
        }));
      }
    } else if (name === 'price_without_vat') {
      if (value && !isNaN(parseFloat(value))) {
        // When price without VAT is entered, calculate price with VAT
        const priceWithVat = calculatePriceWithVat(value);
        setFormData(prev => ({
          ...prev,
          price_with_vat: priceWithVat
        }));
      } else {
        // When price without VAT is cleared, clear price with VAT too
        setFormData(prev => ({
          ...prev,
          price_with_vat: ''
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Define the order of required fields for auto-scroll
    const requiredFields = [
      'name_en',
      'name_ar', 
      'main_unit',
      'quantity_per_main_unit',
      'quantity_per_piece',
      'sub_unit',
      'price_without_vat'
    ];
    
    if (!formData.name_en.trim()) {
      newErrors.name_en = t('validation.required');
    }
    
    if (!formData.name_ar.trim()) {
      newErrors.name_ar = t('validation.required');
    }
    
    // Main Unit validation
    if (!formData.main_unit.trim()) {
      newErrors.main_unit = t('validation.required');
    }
    
    // Quantity per Main Unit validation
    if (!formData.quantity_per_main_unit.trim()) {
      newErrors.quantity_per_main_unit = t('validation.required');
    } else if (isNaN(parseFloat(formData.quantity_per_main_unit)) || parseFloat(formData.quantity_per_main_unit) <= 0) {
      newErrors.quantity_per_main_unit = t('common.pleaseEnterValidPositiveNumber');
    }
    
    // Quantity per Piece validation
    if (!formData.quantity_per_piece.trim()) {
      newErrors.quantity_per_piece = t('validation.required');
    } else if (isNaN(parseFloat(formData.quantity_per_piece)) || parseFloat(formData.quantity_per_piece) <= 0) {
      newErrors.quantity_per_piece = t('common.pleaseEnterValidPositiveNumber');
    }
    
    // Sub Unit validation
    if (!formData.sub_unit.trim()) {
      newErrors.sub_unit = t('validation.required');
    }
    
    // Price without VAT validation
    if (!formData.price_without_vat.trim()) {
      newErrors.price_without_vat = t('validation.required');
    } else if (isNaN(parseFloat(formData.price_without_vat)) || parseFloat(formData.price_without_vat) < 0) {
      newErrors.price_without_vat = t('common.pleaseEnterValidNumber');
    }
    
    // Calories validation
    if (formData.calories && formData.calories.trim()) {
      if (isNaN(parseFloat(formData.calories)) || parseFloat(formData.calories) < 0) {
        newErrors.calories = t('common.pleaseEnterValidNumberZeroOrPositive');
      }
    }
    
    setErrors(newErrors);
    
    // Auto-scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = requiredFields.find(field => newErrors[field]);
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
            element.focus();
          }
        }, 100);
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== FORM SUBMIT START ===');
    console.log('Form submit triggered');
    console.log('Form data before validation:', formData);
    console.log('Selected file:', selectedFile ? selectedFile.name : 'none');
    
    try {
      const validationResult = validateForm();
      console.log('Validation result:', validationResult);
      
      if (!validationResult) {
        console.log('Form validation failed');
        console.log('Validation errors:', errors);
        console.log('=== FORM SUBMIT END (VALIDATION FAILED) ===');
        return;
      }
      
      console.log('Form validation passed');
      
      // Track uploaded image URL to include in submitData (React state updates are async)
      let uploadedImageUrl = formData.image_url;
      
      // If image was removed (image_url is empty but there was an original image), delete from Cloudinary
      if (!formData.image_url && originalImagePublicId && material?.image_url) {
        try {
          console.log('🗑️ Image was removed: Deleting from Cloudinary:', originalImagePublicId);
          await uploadAPI.deleteImage(originalImagePublicId);
          console.log('✅ Image deleted from Cloudinary successfully');
          // Clear originalImagePublicId after deletion
          setOriginalImagePublicId(null);
        } catch (deleteError) {
          console.error('❌ Error deleting image from Cloudinary:', deleteError);
          // Continue with save even if deletion fails - the URL will be cleared in DB
        }
      }
      
      // If there's a selected file, upload it first before saving
      if (selectedFile) {
        console.log('📤 Uploading image before saving material...');
        setUploading(true);
        
        try {
          // If replacing an existing image (or removed image and now adding new one), delete the old one first
          const oldPublicId = originalImagePublicId || uploadedImagePublicId;
          if (oldPublicId) {
            try {
              console.log('🧹 Replacing/removing old image: Deleting from Cloudinary:', oldPublicId);
              await uploadAPI.deleteImage(oldPublicId);
              console.log('✅ Old image deleted from Cloudinary successfully');
              // Clear originalImagePublicId after deletion
              if (originalImagePublicId) {
                setOriginalImagePublicId(null);
              }
            } catch (deleteError) {
              console.error('❌ Error deleting old image from Cloudinary:', deleteError);
              // Continue with uploading new image even if old image deletion fails
            }
          }

          // Upload the selected file to Cloudinary
          const controller = new AbortController();
          setUploadController(controller);
          
          const result = await uploadAPI.uploadImage(selectedFile, controller.signal, 'materials');
          console.log('✅ Image uploaded to Cloudinary:', result.imageUrl);
          
          // Store uploaded image URL for use in submitData
          uploadedImageUrl = result.imageUrl;
          
          // Update form data with uploaded image URL
          setFormData(prev => ({
            ...prev,
            image_url: result.imageUrl
          }));
          
          setUploadedImagePublicId(result.publicId);
          setImagePreview(result.imageUrl); // Update preview to use uploaded URL
          setSelectedFile(null); // Clear selected file since it's now uploaded
          imageDeletedRef.current = false;
          
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log('Upload cancelled by user');
            return; // Don't proceed with save if upload was cancelled
          } else {
            console.error('Error uploading image:', error);
            setErrorMessage(error.message || t('common.failedToUploadImage'));
            setShowErrorModal(true);
            setUploading(false);
            setUploadController(null);
            return; // Don't proceed with save if upload failed
          }
        } finally {
          setUploading(false);
          setUploadController(null);
        }
      } else if (!formData.image_url && originalImagePublicId && material?.image_url) {
        // If image was removed (no selectedFile, image_url is empty, but there was an original image)
        // Delete from Cloudinary and clear it from database
        try {
          console.log('🗑️ Image was removed: Deleting from Cloudinary:', originalImagePublicId);
          await uploadAPI.deleteImage(originalImagePublicId);
          console.log('✅ Image deleted from Cloudinary successfully');
          // Clear originalImagePublicId after deletion
          setOriginalImagePublicId(null);
        } catch (deleteError) {
          console.error('❌ Error deleting image from Cloudinary:', deleteError);
          // Continue with save even if deletion fails - the URL will be cleared in DB
        }
      }
      
      // Convert empty string category_id to 0 for database (Unnamed category)
      // Ensure both price fields are set
      // Use uploadedImageUrl directly (not from state which may not be updated yet)
      const submitData = {
        ...formData,
        image_url: uploadedImageUrl, // Use the uploaded image URL directly
        category_id: formData.category_id || 0,
        price_with_vat: formData.price_with_vat || formData.price_without_vat,
        price_without_vat: formData.price_without_vat || formData.price_with_vat
      };
      
      console.log('📤 Submit data includes image_url:', submitData.image_url);
      
      console.log('Form submission data:', submitData);
      console.log('serving_size value:', submitData.serving_size);
      console.log('Current formData state:', formData);
      console.log('About to call onSave function...');
      console.log('onSave function:', typeof onSave);
      
      onSave(submitData);
      console.log('onSave function called successfully');
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    }
    
    console.log('=== FORM SUBMIT END ===');
    
    // Clear the uploaded image tracking since form was saved successfully
    setUploadedImagePublicId(null);
    setOriginalImagePublicId(null);
  };

  const subUnitOptions = ['g', 'ml', 'piece'];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrorMessage(t('common.pleaseSelectImageFile'));
      setShowErrorModal(true);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage(t('common.imageSizeLimit'));
      setShowErrorModal(true);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Store the file for later upload (when Save is clicked)
    setSelectedFile(file);
    imageDeletedRef.current = false; // Reset deletion flag for new file

    // Create a local preview using FileReader
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result); // This will be a data URL for preview
    };
    reader.onerror = () => {
      setErrorMessage(t('common.failedToPreviewImage'));
      setShowErrorModal(true);
    };
    reader.readAsDataURL(file);
    
    console.log('📁 File selected for preview:', file.name);
    console.log('📁 Upload will happen when Save button is clicked');
  };

  // Cancel upload is no longer needed since upload happens on save
  // Image is only selected for preview until Save is clicked

  const removeImage = () => {
    console.log('🗑️ Remove image button clicked');
    console.log('Current uploadedImagePublicId:', uploadedImagePublicId);
    console.log('Current originalImagePublicId:', originalImagePublicId);
    console.log('Current selectedFile:', selectedFile);
    console.log('Current material?.image_url:', material?.image_url);
    console.log('Current formData.image_url:', formData.image_url);
    console.log('Material exists:', !!material);
    console.log('Is editing mode:', !!material?.image_url);
    
    // Only clear local state - don't delete from Cloudinary yet
    // Deletion will happen when Save is clicked
    console.log('🧹 Clearing local state (deletion from Cloudinary will happen on Save)...');
    setFormData(prev => ({
      ...prev,
      image_url: '' // Clear image URL so it gets saved as empty
    }));
    setImagePreview(null);
    setSelectedFile(null); // Clear selected file if removing image
    setUploadedImagePublicId(null);
    // Keep originalImagePublicId so we know what to delete on Save
    // Only clear it if there's no original image (new material)
    if (!material?.image_url) {
      setOriginalImagePublicId(null);
    }
    imageDeletedRef.current = false; // Reset deletion flag
    console.log('✅ Local state cleared. Image will be deleted from Cloudinary when Save is clicked.');
  };



  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleOverlayClick}>
      <div className="modal-content bg-card rounded-xl shadow-lg border border-border w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" ref={modalContentRef}>
        {/* Header with better spacing */}
        <div className="flex-shrink-0 flex justify-between items-center pt-6 pb-2 px-6">
          <h2 className="text-2xl font-semibold text-card-foreground">
            {material ? t('materials.editMaterial') : t('materials.addNew')}
          </h2>
          <button
            onClick={() => {
              // Don't await cleanup - let it run in background
              cleanupUploadedImage();
              setIsModalOpen(false);
              onCancel();
            }}
            className="text-muted-foreground hover:text-card-foreground p-2 rounded-md hover:bg-muted transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          {/* Scrollable Content Area */}
          <div 
            ref={scrollableAreaRef}
            className="flex-1 overflow-y-auto overscroll-contain bg-background"
            style={{ minHeight: 0 }}
            tabIndex={-1}
          >
            {/* Material Details Tab */}
            <div className="p-8 flex flex-col">
              <div className="max-w-7xl mx-auto w-full space-y-8">
                {/* Material Details Card */}
                <div className="bg-card rounded-xl shadow-sm border border-border p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-card-foreground">{t('common.materialInformation')}</h3>
                      <p className="text-sm text-muted-foreground">{t('common.basicDetails')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Material Name (EN) */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.nameEn')} *
                      </label>
                      <input
                        type="text"
                        name="name_en"
                        value={formData.name_en}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.name_en ? 'border-destructive' : ''}`}
                        placeholder="e.g., Tomato"
                      />
                      {errors.name_en && (
                        <p className="text-destructive text-sm mt-1">{errors.name_en}</p>
                      )}
                    </div>

                    {/* Material Name (AR) */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.nameAr')} *
                      </label>
                      <input
                        type="text"
                        name="name_ar"
                        value={formData.name_ar}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.name_ar ? 'border-destructive' : ''}`}
                        placeholder="e.g., طماطم"
                      />
                      {errors.name_ar && (
                        <p className="text-destructive text-sm mt-1">{errors.name_ar}</p>
                      )}
                    </div>

                    {/* Brand Name */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.brandName')}
                      </label>
                      <input
                        type="text"
                        name="brand_name"
                        value={formData.brand_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground"
                        placeholder="e.g., Fresh Market"
                      />
                    </div>

                    {/* Main Unit */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.mainUnit')} *
                      </label>
                      <input
                        type="text"
                        name="main_unit"
                        value={formData.main_unit}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.main_unit ? 'border-destructive' : ''}`}
                        placeholder="e.g., carton, box"
                      />
                      {errors.main_unit && (
                        <p className="text-destructive text-sm mt-1">{errors.main_unit}</p>
                      )}
                    </div>

                    {/* Quantity per Main Unit */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.quantityPerMainUnit')} *
                      </label>
                      <input
                        type="text"
                        name="quantity_per_main_unit"
                        value={formData.quantity_per_main_unit}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.quantity_per_main_unit ? 'border-destructive' : ''}`}
                        placeholder="e.g., 24"
                      />
                      {errors.quantity_per_main_unit && (
                        <p className="text-destructive text-sm mt-1">{errors.quantity_per_main_unit}</p>
                      )}
                    </div>

                    {/* Quantity per Piece */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.quantityPerPiece')} *
                      </label>
                      <input
                        type="text"
                        name="quantity_per_piece"
                        value={formData.quantity_per_piece}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.quantity_per_piece ? 'border-destructive' : ''}`}
                        placeholder="e.g., 400"
                      />
                      {errors.quantity_per_piece && (
                        <p className="text-destructive text-sm mt-1">{errors.quantity_per_piece}</p>
                      )}
                    </div>

                    {/* Sub Unit */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.subUnit')} *
                      </label>
                      <div className="relative" ref={subUnitDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsSubUnitOpen(!isSubUnitOpen)}
                          className={`w-full flex items-center px-3 py-2 border border-border rounded-lg shadow-sm ${i18n.language === 'ar' ? 'text-right flex-row-reverse' : 'text-left justify-between'} bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors ${errors.sub_unit ? 'border-destructive' : ''}`}
                        >
                          <span className={`${formData.sub_unit ? 'text-card-foreground' : 'text-muted-foreground'} truncate`} title={formData.sub_unit || t('common.selectUnit')}>
                            {formData.sub_unit || t('common.selectUnit')}
                          </span>
                          <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {isSubUnitOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto overflow-x-hidden">
                            <div className="py-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, sub_unit: '' }));
                                  setIsSubUnitOpen(false);
                                }}
                                className={`w-full px-3 py-2 text-sm text-card-foreground hover:bg-muted ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                              >
                                {t('common.selectUnit')}
                              </button>
                              {subUnitOptions.map(unit => (
                                <button
                                  key={unit}
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, sub_unit: unit }));
                                    if (errors.sub_unit) {
                                      setErrors(prev => ({ ...prev, sub_unit: '' }));
                                    }
                                    setIsSubUnitOpen(false);
                                  }}
                                  className={`w-full px-3 py-2 text-sm hover:bg-muted ${
                                    formData.sub_unit === unit ? 'bg-primary/20 text-primary' : 'text-card-foreground'
                                  } ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                                  title={unit}
                                >
                                  <span className="truncate block">{unit}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.sub_unit && (
                        <p className="text-destructive text-sm mt-1">{errors.sub_unit}</p>
                      )}
                    </div>

                    {/* Price without VAT */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.priceOfMainUnit')} *
                        <span 
                          className="ml-2 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-card-foreground bg-muted hover:bg-muted/80 transition-colors"
                          title="VAT Calculation: Enter either price without VAT or price with VAT. The other field will be calculated automatically using the current VAT rate. Clearing either field will clear both price fields."
                        >
                          !
                        </span>
                      </label>
                      <input
                        type="text"
                        name="price_without_vat"
                        value={formData.price_without_vat}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.price_without_vat ? 'border-destructive' : ''}`}
                        placeholder="e.g., 41.74"
                      />
                      {errors.price_without_vat && (
                        <p className="text-destructive text-sm mt-1">{errors.price_without_vat}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('common.enterPriceExcludingVat')}
                      </p>
                    </div>

                    {/* Price with VAT */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.priceWithVat')}
                      </label>
                      <input
                        type="text"
                        name="price_with_vat"
                        value={formData.price_with_vat}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.price_with_vat ? 'border-destructive' : ''}`}
                        placeholder="e.g., 48.00"
                      />
                      {errors.price_with_vat && (
                        <p className="text-destructive text-sm mt-1">{errors.price_with_vat}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('common.calculatedAutomatically')}
                      </p>
                    </div>


                    {/* Supplier */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.supplier')}
                      </label>
                      <input
                        type="text"
                        name="supplier"
                        value={formData.supplier}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground"
                        placeholder="e.g., ABC Supplier"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('materials.category')}
                      </label>
                      <div className="relative" ref={categoryDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                          className={`w-full flex items-center px-3 py-2 border border-border rounded-lg shadow-sm ${i18n.language === 'ar' ? 'text-right flex-row-reverse' : 'text-left justify-between'} bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors`}
                        >
                          <span className={`${formData.category_id ? 'text-card-foreground' : 'text-muted-foreground'} truncate`} title={formData.category_id ? (i18n.language === 'ar' ? categories.find(cat => cat.id === formData.category_id)?.name_ar : categories.find(cat => cat.id === formData.category_id)?.name_en) : t('common.selectCategory')}>
                            {formData.category_id ? 
                              (i18n.language === 'ar' ? categories.find(cat => cat.id === formData.category_id)?.name_ar : categories.find(cat => cat.id === formData.category_id)?.name_en) : 
                              t('common.selectCategory')
                            }
                          </span>
                          <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {isCategoryOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto overflow-x-hidden">
                            <div className="py-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, category_id: '' }));
                                  setIsCategoryOpen(false);
                                }}
                                className={`w-full px-3 py-2 text-sm text-card-foreground hover:bg-muted ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                              >
                                {t('common.selectCategory')}
                              </button>
                              {categories.map(category => (
                                <button
                                  key={category.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, category_id: category.id }));
                                    setIsCategoryOpen(false);
                                  }}
                                  className={`w-full px-3 py-2 text-sm hover:bg-muted ${
                                    formData.category_id === category.id ? 'bg-primary/20 text-primary' : 'text-card-foreground'
                                  } ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                                  title={i18n.language === 'ar' ? category.name_ar : category.name_en}
                                >
                                  <span className="truncate block">{i18n.language === 'ar' ? category.name_ar : category.name_en}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Material Image */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('common.materialImage')}
                      </label>
                      <div className={`flex items-center space-x-4 ${i18n.language === 'ar' ? 'justify-end' : ''}`}>
                        <div className="flex items-center space-x-3">
                          <label 
                            className={`cursor-pointer inline-flex items-center px-4 py-2 border border-border rounded-lg shadow-sm text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors ${uploading || imagePreview ? 'opacity-50 cursor-not-allowed' : ''} ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <Upload className={`h-4 w-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                            {t('common.uploadImage')}
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleFileSelect}
                              className="hidden"
                              disabled={uploading}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          </label>
                        </div>
                        
                        {/* Image Preview */}
                        {imagePreview && (
                          <div className="relative inline-block">
                            <img
                              src={imagePreview}
                              alt="Material preview"
                              className="w-16 h-16 object-cover rounded-lg border-2 border-border"
                            />
                            <button
                              type="button"
                              onClick={removeImage}
                              className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90 transition-colors shadow-lg border-2 border-card"
                              title={t('common.removeImage')}
                            >
                              <XIcon className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Nutritional Information Card */}
                <div className="bg-card rounded-xl shadow-sm border border-border p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-chart-2/20 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-chart-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-card-foreground">{t('common.nutritionalInformation')}</h3>
                      <p className="text-sm text-muted-foreground">{t('common.calorieAndServingInfo')}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Serving Size */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('common.servingSize')}
                      </label>
                      <input
                        type="text"
                        name="serving_size"
                        value={formData.serving_size || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground"
                        placeholder="e.g., 100g, 1 tbsp, 1 cup, 1 piece"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('common.thisDefinesBaseUnit')}
                      </p>
                    </div>

                    {/* Calories */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('common.caloriesPerServing')}
                      </label>
                      <input
                        type="text"
                        name="calories"
                        value={formData.calories}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.calories ? 'border-destructive' : ''}`}
                        placeholder="e.g., 25"
                      />
                      {errors.calories && (
                        <p className="text-destructive text-sm mt-1">{errors.calories}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('common.willBeDisplayedAsWholeNumbers')}
                      </p>
                    </div>

                    {/* Allergens */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        {t('common.allergens')}
                      </label>
                      <AllergenMultiSelect
                        selectedAllergens={formData.allergens}
                        onChange={(allergens) => setFormData(prev => ({ ...prev, allergens }))}
                        placeholder={t('common.selectAllergens')}
                        className="w-full"
                        openUpward={true}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('common.selectAllergensPresent')}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex-shrink-0 px-6 pb-6 pt-4 border-t border-border bg-card">
            <div className={`flex justify-end ${i18n.language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-3'}`}>
              <button
                type="button"
                onClick={() => {
                  // Don't await cleanup - let it run in background
                  cleanupUploadedImage();
                  setIsModalOpen(false);
                  onCancel();
                }}
                className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
                disabled={uploading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={uploading}
                onClick={(e) => {
                  console.log('Save button clicked!');
                  console.log('Button disabled:', uploading);
                  console.log('Button type:', 'submit');
                  console.log('Event:', e);
                  
                  // Manual form submission test
                  const form = e.target.closest('form');
                  console.log('Found form element:', form);
                  if (form) {
                    console.log('Form onSubmit handler:', form.onsubmit);
                    console.log('Form action:', form.action);
                    console.log('Form method:', form.method);
                    
                    // Try to manually trigger form submission
                    setTimeout(() => {
                      console.log('Attempting manual form submission...');
                      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                      form.dispatchEvent(submitEvent);
                    }, 100);
                  }
                }}
              >
                {uploading ? t('common.uploading') : t('common.save')}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Error Modal Popup */}
      {showErrorModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card w-full max-w-md rounded-xl shadow-2xl border border-border/50 p-8 mx-4 transform transition-all duration-300 ease-out scale-100">
            {/* Header with icon */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
                <h4 className="text-lg font-semibold text-card-foreground">{t('common.error')}</h4>
              </div>
              <button 
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted/50" 
                onClick={() => setShowErrorModal(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Message */}
            <div className="mb-8">
              <p className="text-sm text-card-foreground leading-relaxed">{errorMessage}</p>
            </div>
            
            {/* Action button */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-6 py-2.5 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors shadow-sm hover:shadow-md"
              >
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialForm; 